import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { StringKey } from '../../../lang/index';

export enum ShareConfirmVariant {
  CANCEL_EDIT = 'cancel-edit',
  DELETE = 'delete',
  SAVE_EDIT = 'save-edit',
}

type ConfirmCopy = {
  confirmLabel: StringKey;
  descriptionId: StringKey;
  isDestructive: boolean;
  titleId: StringKey;
};

const COPY: Record<ShareConfirmVariant, ConfirmCopy> = {
  [ShareConfirmVariant.CANCEL_EDIT]: {
    confirmLabel: 'SHARE_CONFIRM_DISCARD',
    descriptionId: 'SHARE_CONFIRM_CANCEL_DESCRIPTION',
    isDestructive: false,
    titleId: 'SHARE_CONFIRM_CANCEL_TITLE',
  },
  [ShareConfirmVariant.DELETE]: {
    confirmLabel: 'DELETE',
    descriptionId: 'SHARE_CONFIRM_DELETE_DESCRIPTION',
    isDestructive: true,
    titleId: 'SHARE_CONFIRM_DELETE_TITLE',
  },
  [ShareConfirmVariant.SAVE_EDIT]: {
    confirmLabel: 'SAVE',
    descriptionId: 'SHARE_CONFIRM_SAVE_DESCRIPTION',
    isDestructive: false,
    titleId: 'SHARE_CONFIRM_SAVE_TITLE',
  },
};

type ShareConfirmModalProps = {
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  variant: ShareConfirmVariant;
};

export const ShareConfirmModal: FC<ShareConfirmModalProps> = ({
  isPending = false,
  onCancel,
  onConfirm,
  variant,
}) => {
  const { confirmLabel, descriptionId, isDestructive, titleId } = COPY[variant];

  return (
    // No `min-w`: on a 320px phone a 300px floor plus the gutters is wider than the screen, and
    // `w-full` already holds the modal at its full width everywhere else.
    <div className="m-2 flex w-full flex-col gap-8 rounded bg-white p-4 shadow-3xl xlg:w-[453px]">
      <div className="flex flex-col gap-1">
        <span className="text-xl font-semibold text-gray-700">
          <AppFormattedMessage id={titleId} />
        </span>
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id={descriptionId} />
        </span>
      </div>

      <div className="flex h-11 items-center justify-between gap-4">
        <Button
          className="h-full w-fit rounded px-3 py-[6px] text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className={`h-full w-fit px-6 py-[10px] text-sm font-[550] text-white ${
            isDestructive ? 'bg-fireside-500' : ''
          }`}
          disabled={isPending}
          isLoading={isPending}
          onClick={onConfirm}
          {...(isDestructive ? { styleType: 'NONE' as const } : {})}
        >
          <AppFormattedMessage id={confirmLabel} />
        </Button>
      </div>
    </div>
  );
};
